import colorPallete, { bodyFonts, buttonFonts, themeFontWeights } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img/Img";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import styled, { css } from "styled-components";

const enum fields {
    sectionHeaderTitle = "sectionHeaderTitle",
    sectionFooterDescription = "sectionFooterDescription",
    eventCardOneTitle = "eventCardOneTitle",
    eventCardTwoTitle = "eventCardTwoTitle",
    eventCardThreeTitle = "eventCardThreeTitle",
    eventCardOneImage = "eventCardOneImage",
    eventCardTwoImage = "eventCardTwoImage",
    eventCardThreeImage = "eventCardThreeImage",
    eventCardOneImageAltText = "eventCardOneImageAltText",
    eventCardTwoImageAltText = "eventCardTwoImageAltText",
    eventCardThreeImageAltText = "eventCardThreeImageAltText",
    eventCardOneDate = "eventCardOneDate",
    eventCardTwoDate = "eventCardTwoDate",
    eventCardThreeDate = "eventCardThreeDate",
    eventCardOneDescription = "eventCardOneDescription",
    eventCardTwoDescription = "eventCardTwoDescription",
    eventCardThreeDescription = "eventCardThreeDescription",
}

interface Props extends WidgetProps {
    fields: {
        [fields.sectionHeaderTitle]: string;
        [fields.sectionFooterDescription]: string;
        [fields.eventCardOneTitle]: string;
        [fields.eventCardTwoTitle]: string;
        [fields.eventCardThreeTitle]: string;
        [fields.eventCardOneImage]: string;
        [fields.eventCardTwoImage]: string;
        [fields.eventCardThreeImage]: string;
        [fields.eventCardOneImageAltText]: string;
        [fields.eventCardTwoImageAltText]: string;
        [fields.eventCardThreeImageAltText]: string;
        [fields.eventCardOneDate]: Date;
        [fields.eventCardTwoDate]: Date;
        [fields.eventCardThreeDate]: Date;
        [fields.eventCardOneDescription]: string;
        [fields.eventCardTwoDescription]: string;
        [fields.eventCardThreeDescription]: string;
    };
}

export interface UpcomingEventsStyles {
    sectionHeaderTitle?: TypographyPresentationProps;
    sectionFooterDescription?: TypographyPresentationProps;
    eventsCarouselContainer?: InjectableCss;
    eventsSlideWrapper?: InjectableCss;
    eventTitle?: TypographyPresentationProps;
    eventDate?: TypographyPresentationProps;
    eventDescription?: TypographyPresentationProps;
}

export const upcomingEventsStyles: UpcomingEventsStyles = {
    sectionHeaderTitle: {
        variant: "h2",
        css: css`
            text-align: center;
        `,
    },
    sectionFooterDescription: {
        css: css`
            padding: 0 16px;

            &:empty {
                display: none;
            }

            p[class*="TypographyStyle"] {
                margin-bottom: 0;
                line-height: 24px;
            }

            .events-footer {
                display: flex;
                align-items: center;
                justify-content: center;
                column-gap: 24px;

                .social-icons {
                    flex-shrink: 0;
                    > [class*="StyledClickable"] {
                        &:not(:last-child) {
                            margin-right: 24px;
                        }
                    }
                }

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                flex-direction: column;
                                row-gap: 24px;
                                padding: 0 30px;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            }
        `,
    },
    eventsCarouselContainer: {
        css: css`
            display: flex;
            column-gap: 40px;
            padding: 24px;
            justify-content: center;

            @media only screen and (max-width: 800px) {
                justify-content: flex-start;
                overflow-x: auto;
                scroll-snap-type: x mandatory;
                overscroll-behavior-x: contain;
                padding: 16px 24px;
                column-gap: 24px;
                &::-webkit-scrollbar {
                    display: none;
                }
            }
        `,
    },
    eventsSlideWrapper: {
        css: css`
            background: ${colorPallete.grey100};
            border-radius: 8px;
            max-width: 256px;
            padding: 16px;
            scroll-snap-align: center;
            img {
                margin-bottom: 24px;
                width: 100%;
                min-width: 224px;
                max-width: 224px;
                max-height: 183px;
                min-height: 183px;
                border-radius: 8px;
                object-fit: cover;
                display: flex;
            }
        `,
    },
    eventTitle: {
        size: buttonFonts.buttonLg.fontSize,
        weight: buttonFonts.buttonLg.fontWeight,
        lineHeight: buttonFonts.buttonLg.lineHeight,
        color: colorPallete.primaryRed500,
        css: css`
            display: inline-block;
            color: ${colorPallete.primaryRed500};
            margin-bottom: 8px;
        `,
    },
    eventDate: {
        size: bodyFonts.body2RegularLg.fontSize,
        weight: bodyFonts.body2RegularLg.fontWeight,
        lineHeight: bodyFonts.body2RegularLg.lineHeight,
    },
    eventDescription: {
        size: bodyFonts.body2RegularLg.fontSize,
        weight: bodyFonts.body2RegularLg.fontWeight,
        lineHeight: bodyFonts.body2RegularLg.lineHeight,
    },
};

const Wrapper = styled.div`
    max-width: 1280px;
    margin: 0 auto;
    padding: 48px 0;
    height: 100%;
    border-top: 1px solid ${colorPallete.grey300};
    @media only screen and (max-width: 768px) {
        padding: 40px 0;
    }

    &:empty {
        display: none;
    }
`;

const Image = styled.img`

`;

const DsgUpcomingEvents: React.FC<Props> = props => {
    const styles = upcomingEventsStyles;

    const isEventValid = (date: Date) => {
        const eventDate = new Date(date);
        const currentDate = new Date();
        return eventDate >= currentDate;
    };

    const getStandardDateFormat = (isoDateString: Date) => {
        const date = new Date(isoDateString);
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, "0");
        const day = String(date.getUTCDate()).padStart(2, "0");

        const formattedDate = `${month}/${day}/${year}`;

        return formattedDate;
    };

    return (
        <Wrapper>
            {props.fields.sectionHeaderTitle !== "" && (
                <Typography {...styles.sectionHeaderTitle}>{props.fields.sectionHeaderTitle}</Typography>
            )}
            <StyledWrapper {...styles.eventsCarouselContainer}>
                {isEventValid(props.fields.eventCardOneDate) && (
                    <StyledWrapper {...styles.eventsSlideWrapper}>
                        <Img src={props.fields.eventCardOneImage} altText={props.fields.eventCardOneImageAltText || ''} />
                        <Typography {...styles.eventTitle}>{props.fields.eventCardOneTitle}</Typography>
                        <Typography as="p" {...styles.eventDate}>
                            {getStandardDateFormat(props.fields.eventCardOneDate)}
                        </Typography>
                        <Typography as="p" {...styles.eventDescription}>
                            {props.fields.eventCardOneDescription}
                        </Typography>
                    </StyledWrapper>
                )}

                {isEventValid(props.fields.eventCardTwoDate) && (
                    <StyledWrapper {...styles.eventsSlideWrapper}>
                        <Img src={props.fields.eventCardTwoImage} altText={props.fields.eventCardOneImageAltText || ''} />
                        <Typography {...styles.eventTitle}>{props.fields.eventCardTwoTitle}</Typography>
                        <Typography as="p" {...styles.eventDate}>
                            {getStandardDateFormat(props.fields.eventCardTwoDate)}
                        </Typography>
                        <Typography as="p" {...styles.eventDescription}>
                            {props.fields.eventCardTwoDescription}
                        </Typography>
                    </StyledWrapper>
                )}

                {isEventValid(props.fields.eventCardThreeDate) && (
                    <StyledWrapper {...styles.eventsSlideWrapper}>
                        <Img src={props.fields.eventCardThreeImage} altText={props.fields.eventCardOneImageAltText || ''} />
                        <Typography {...styles.eventTitle}>{props.fields.eventCardThreeTitle}</Typography>
                        <Typography as="p" {...styles.eventDate}>
                            {getStandardDateFormat(props.fields.eventCardThreeDate)}
                        </Typography>
                        <Typography as="p" {...styles.eventDescription}>
                            {props.fields.eventCardThreeDescription}
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>

            <Typography forwardAs="div" {...styles.sectionFooterDescription}>
                {parse(props.fields.sectionFooterDescription, parserOptions)}
            </Typography>
        </Wrapper>
    );
};

const basicTab = {
    displayName: "Common",
    sortOrder: 0,
};

const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        {
            name: fields.sectionHeaderTitle,
            displayName: "Section Header",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "text tip",
            placeholder: "Enter Title",
            tab: basicTab,
        },
        {
            name: fields.eventCardOneImage,
            fieldType: "Contextual",
            displayName: "Card One Image",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            tab: basicTab,
        },
        {
            name: fields.eventCardOneImageAltText,
            fieldType: "Contextual",
            displayName: "Card One Image Alt Text",
            editorTemplate: "TextField",
            defaultValue: "",
            tab: basicTab,
        },
        {
            name: fields.eventCardOneTitle,
            displayName: "Card One Title",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "Enter Title",
            tab: basicTab,
        },
        {
            name: fields.eventCardOneDate,
            displayName: "Card One Date",
            editorTemplate: "DateTimeField",
            defaultValue: null,
            fieldType: "General",
            tab: basicTab,
        },
        {
            name: fields.eventCardOneDescription,
            displayName: "Card One Short Description",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "Enter Descriptions",
            tab: basicTab,
        },
        {
            name: fields.eventCardTwoImage,
            fieldType: "Contextual",
            displayName: "Card Two Image",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            tab: basicTab,
        },
        {
            name: fields.eventCardTwoImageAltText,
            fieldType: "Contextual",
            displayName: "Card Two Image Alt Text",
            editorTemplate: "TextField",
            defaultValue: "",
            tab: basicTab,
        },
        {
            name: fields.eventCardTwoTitle,
            displayName: "Card Two Title",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "Enter Title",
            tab: basicTab,
        },
        {
            name: fields.eventCardTwoDate,
            displayName: "Card Two Date",
            editorTemplate: "DateTimeField",
            defaultValue: null,
            fieldType: "General",
            tab: basicTab,
        },
        {
            name: fields.eventCardTwoDescription,
            displayName: "Card Two Short Description",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "Enter Descriptions",
            tab: basicTab,
        },
        {
            name: fields.eventCardThreeImage,
            fieldType: "Contextual",
            displayName: "Card Three Image",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            tab: basicTab,
        },
        {
            name: fields.eventCardThreeImageAltText,
            fieldType: "Contextual",
            displayName: "Card Three Image Alt Text",
            editorTemplate: "TextField",
            defaultValue: "",
            tab: basicTab,
        },
        {
            name: fields.eventCardThreeTitle,
            displayName: "Card Three Title",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "Enter Title",
            tab: basicTab,
        },
        {
            name: fields.eventCardThreeDate,
            displayName: "Card Three Date",
            editorTemplate: "DateTimeField",
            defaultValue: null,
            fieldType: "General",
            tab: basicTab,
        },
        {
            name: fields.eventCardThreeDescription,
            displayName: "Card Three Short Description",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "Enter Descriptions",
            tab: basicTab,
        },
        {
            name: fields.sectionFooterDescription,
            fieldType: "Translatable",
            editorTemplate: "RichTextField",
            displayName: "Section Footer Description",
            defaultValue: "",
            extendedConfig: { height: 170 },
            expandedToolbarButtons: {
                moreMisc: {
                    buttons: [
                        "insertLink",
                        "insertImage",
                        "insertHR",
                        "specialCharacters",
                        "insertVideo",
                        "insertFile",
                    ],
                    buttonsVisible: 3,
                },
                code: {
                    buttons: ["selectAll", "html", "help"],
                    buttonsVisible: 3,
                },
            },
            tab: basicTab,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: DsgUpcomingEvents,
    definition,
};

export default widgetModule;
